import { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Flame, ArrowRight, ArrowLeft, FileDown, Loader2, CheckCircle, Eye, Save } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import { GAS_FORMS } from "@/config/gasFormConfigs";
import { saveDraft, loadDraft, deleteDraft } from "@/utils/offlineStorage";
import { queueForSync } from "@/utils/syncQueue";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { useCreditPrompt } from "@/hooks/useCreditPrompt";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import SignaturePad from "@/components/SignaturePad";
import TemplateBar, { SaveTemplateButton } from "@/components/TemplateBar";
import { authFetch } from "@/utils/authFetch";

const API = process.env.REACT_APP_BACKEND_URL;

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>{children}</label>
);

function FormField({ field, value, onChange }) {
  if (field.type === "checkbox") {
    return (
      <label className="flex items-start gap-3 py-2 cursor-pointer group" data-testid={`gas-field-${field.id}`}>
        <Checkbox checked={!!value} onCheckedChange={(c) => onChange(!!c)}
          className="mt-0.5 w-5 h-5 border-2 rounded-none data-[state=checked]:bg-[#EF4444] data-[state=checked]:border-[#EF4444] flex-shrink-0"
          style={{ borderColor: 'var(--border-main)' }} />
        <span className="text-sm leading-snug" style={{ color: 'var(--text-primary)' }}>{field.label}</span>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <div className="form-field" data-testid={`gas-field-${field.id}`}>
        <Label>{field.label}</Label>
        <select className="brutalist-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select...</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="form-field" data-testid={`gas-field-${field.id}`}>
        <Label>{field.label}</Label>
        <textarea className="brutalist-input min-h-[60px] resize-y" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || ""} />
      </div>
    );
  }
  return (
    <div className="form-field" data-testid={`gas-field-${field.id}`}>
      <Label>{field.label}</Label>
      <input className="brutalist-input" type={field.type === "number" ? "number" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || ""} />
    </div>
  );
}

export default function GasSafeGenericWizard() {
  const { formId } = useParams();
  const { user } = useAuth();
  const config = GAS_FORMS[formId];

  const [step, setStep] = useState(1);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [signature, setSignature] = useState("");
  const { showModal, modalCreditType, promptBuyCredits, closeModal, buyCredits } = useCreditPrompt();

  const draftKey = formId ? `draft_gas_${formId}` : null;

  const [project, setProject] = useState({
    project_ref: "", installation_address: "", installation_postcode: "",
    client_name: "", client_email: "", completed_by: user?.name || "", cert_date: null,
    contractor_trading_title: user?.company || "", contractor_email: user?.email || "",
    contractor_phone: "", contractor_address: "", contractor_postcode: "", contractor_reg_no: "",
  });
  const [fields, setFields] = useState({});

  useEffect(() => {
    if (!draftKey || completed) return;
    const timer = setInterval(() => {
      saveDraft(draftKey, { project, fields }).then(() => setDraftSaved(true));
    }, 10000);
    return () => clearInterval(timer);
  }, [project, fields, draftKey, completed]);

  const manualSaveDraft = async () => {
    if (!draftKey) return;
    await saveDraft(draftKey, { project, fields });
    setDraftSaved(true);
    toast.success("Draft saved locally");
  };

  const [editCertId, setEditCertId] = useState(null);

  useEffect(() => {
    // Check for edit data
    const editData = sessionStorage.getItem("edit_cert_data");
    if (editData) {
      try {
        const cert = JSON.parse(editData);
        sessionStorage.removeItem("edit_cert_data");
        setEditCertId(cert._edit_id);
        if (cert.completed_by || cert.contractor_trading_title) setProject((p) => ({ ...p, ...cert, cert_date: cert.cert_date ? new Date(cert.cert_date.split("/").reverse().join("-")) : null }));
        if (cert.form_fields) setFields(cert.form_fields);
        toast.info("Editing document — update and regenerate for free");
        return;
      } catch { /* ignore */ }
    }

    if (draftKey) {
      loadDraft(draftKey).then((draft) => {
        if (draft) {
          if (draft.project) setProject((p) => ({ ...p, ...draft.project }));
          if (draft.fields) setFields(draft.fields);
          toast.info("Restored from saved draft");
          return;
        }
      }).catch(() => {});
    }
    authFetch(`${API}/api/user/contractor-details`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : {})
      .then((saved) => {
        if (saved?.contractor_trading_title) {
          setProject((p) => ({
            ...p,
            contractor_trading_title: saved.contractor_trading_title || p.contractor_trading_title,
            contractor_email: saved.contractor_email || p.contractor_email,
            contractor_phone: saved.contractor_phone || p.contractor_phone,
            contractor_address: saved.contractor_address || p.contractor_address,
            contractor_postcode: saved.contractor_postcode || p.contractor_postcode,
            contractor_reg_no: saved.contractor_reg_no || p.contractor_reg_no,
          }));
        }
      }).catch(() => {});
  }, [draftKey]);

  if (!config) return <Navigate to="/new/gas-safe" replace />;

  const setP = (f) => (e) => setProject((p) => ({ ...p, [f]: e.target.value }));
  const setF = (id, val) => setFields((prev) => ({ ...prev, [id]: val }));

  const STEPS = [{ num: 1, label: "Details & Form" }, { num: 2, label: "Review & Generate" }];

  const buildPayload = () => ({
    ...project,
    cert_date: project.cert_date ? format(project.cert_date, "dd/MM/yyyy") : "",
    form_type: config.type,
    form_title: config.title,
    form_fields: fields,
    signature,
    form_sections: config.sections.map((s) => ({
      title: s.title,
      fields: s.fields.map((f) => ({ id: f.id, label: f.label, type: f.type, value: fields[f.id] ?? "" })),
    })),
  });

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await authFetch(`${API}/api/certificates/gas-safe/preview`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreview(true);
    } catch (err) { toast.error(err.message); } finally { setPreviewing(false); }
  };

  const handleGenerate = async () => {
    if (!navigator.onLine) {
      await queueForSync({ endpoint: "/api/certificates/gas-safe", method: "POST", payload: buildPayload(), formTitle: config.title });
      await manualSaveDraft();
      toast.success("You're offline — certificate queued and will auto-submit when back online.");
      return;
    }
    setSubmitting(true);
    try {
      const isEdit = !!editCertId;
      const url = isEdit ? `${API}/api/certificates/${editCertId}` : `${API}/api/certificates/gas-safe`;
      const method = isEdit ? "PUT" : "POST";
      const res = await authFetch(url, {
        method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (res.status === 402) { promptBuyCredits("gas"); return; }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      const data = await res.json();
      const certId = data.certificate_id || editCertId;
      setPdfUrl(`${API}/api/certificates/domestic-eic/${certId}/download`);
      setCompleted(true);
      toast.success(isEdit ? "Document updated!" : "Document generated! 1 credit used.");
      if (draftKey) deleteDraft(draftKey).catch(() => {});
    } catch (err) {
      // Network failure — queue for sync
      if (!navigator.onLine || err.message === "Failed to fetch") {
        await queueForSync({ endpoint: "/api/certificates/gas-safe", method: "POST", payload: buildPayload(), formTitle: config.title });
        toast.success("Network lost — certificate queued for auto-sync.");
      } else {
        toast.error(err.message);
      }
    } finally { setSubmitting(false); }
  };

  if (completed) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}><AppHeader />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="form-container text-center" data-testid="gas-generic-complete">
            <CheckCircle className="w-16 h-16 text-[#EF4444] mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Document Generated</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>Your {config.title} has been created.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {pdfUrl && <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="brutalist-btn flex items-center justify-center gap-2"><FileDown className="w-4 h-4" /> Download PDF</a>}
              <a href="/dashboard" className="brutalist-btn-outline flex items-center justify-center gap-2">Back to Dashboard</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="gas-generic-wizard">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Flame className="w-6 h-6 text-[#EF4444]" />
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{config.title}</h1>
        </div>

        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 flex items-center justify-center border-2 text-sm font-bold transition-all ${step > s.num ? "bg-[#EF4444] text-white border-[#EF4444]" : step === s.num ? "bg-[#EF4444] text-white border-[#EF4444]" : "bg-white text-[#B8B9BD] border-[#E4E4E7]"}`}>
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className="text-xs mt-2 font-medium text-center hidden sm:block" style={{ fontFamily: 'Outfit, sans-serif', color: step >= s.num ? 'var(--text-primary)' : '#B8B9BD' }}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 sm:mx-4 ${step > s.num ? "bg-[#EF4444]" : "bg-[#E4E4E7]"}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="form-container max-w-3xl mx-auto" data-testid="gas-generic-step-1">
            <TemplateBar formType={config?.type || ""} onLoad={(data) => {
              if (data.project) setProject(p => ({ ...p, ...data.project }));
              if (data.fields) setFields(f => ({ ...f, ...data.fields }));
            }} />
            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Engineer & Contractor</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Completed by</Label><input className="brutalist-input" value={project.completed_by} onChange={setP("completed_by")} /></div>
                <div><Label>Company</Label><input className="brutalist-input" value={project.contractor_trading_title} onChange={setP("contractor_trading_title")} /></div>
                <div><Label>Gas Safe Reg No</Label><input className="brutalist-input" value={project.contractor_reg_no} onChange={setP("contractor_reg_no")} /></div>
                <div>
                  <Label>Date</Label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <button className="brutalist-input flex items-center justify-between cursor-pointer w-full" type="button">
                        <span className={project.cert_date ? "" : "text-[#9CA3AF]"}>{project.cert_date ? format(project.cert_date, "dd/MM/yyyy") : "Select date"}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#0A0A0A] rounded-none" align="start">
                      <Calendar mode="single" selected={project.cert_date} onSelect={(d) => { setProject((p) => ({ ...p, cert_date: d })); setCalendarOpen(false); }} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            {config.sections.map((section, si) => {
              const isAllCheckboxes = section.fields.every((f) => f.type === "checkbox");
              return (
                <div key={si} className="border-b-2 pb-5 mb-5 last:border-0" style={{ borderColor: 'var(--border-light)' }}>
                  <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{section.title}</h3>
                  {isAllCheckboxes ? (
                    <div className="space-y-0">
                      {section.fields.map((f) => (
                        <FormField key={f.id} field={f} value={fields[f.id]} onChange={(v) => setF(f.id, v)} />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {section.fields.map((f) => (
                        f.type === "checkbox" ? (
                          <div key={f.id} className="md:col-span-2">
                            <FormField field={f} value={fields[f.id]} onChange={(v) => setF(f.id, v)} />
                          </div>
                        ) : (
                          <div key={f.id} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                            <FormField field={f} value={fields[f.id]} onChange={(v) => setF(f.id, v)} />
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="flex justify-between items-center">
              <button type="button" onClick={manualSaveDraft} className="brutalist-btn-outline !px-3 !py-1.5 text-xs flex items-center gap-1.5" data-testid="gas-generic-save-draft">
                <Save className="w-3.5 h-3.5" /> {draftSaved ? "Draft Saved" : "Save Draft"}
              </button>
              <button onClick={() => setStep(2)} className="brutalist-btn flex items-center gap-2">Review <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="form-container max-w-3xl mx-auto" data-testid="gas-generic-step-2">
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Review & Generate</h2>
            <p className="text-xs mb-6" style={{ color: 'var(--text-secondary)' }}>Review all details, then generate your document.</p>

            <div className="border-b-2 pb-4 mb-4" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Engineer Details</h3>
              {[["Engineer", project.completed_by], ["Company", project.contractor_trading_title], ["Gas Safe No", project.contractor_reg_no], ["Date", project.cert_date ? format(project.cert_date, "dd/MM/yyyy") : ""]].map(([l, v]) => v ? (
                <div key={l} className="flex justify-between py-1 text-sm">
                  <span style={{ color: 'var(--text-secondary)' }}>{l}</span>
                  <span className="font-semibold text-right max-w-[60%]">{v}</span>
                </div>
              ) : null)}
            </div>

            {config.sections.map((section, si) => (
              <div key={si} className="border-b-2 pb-4 mb-4 last:border-0" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{section.title}</h3>
                {section.fields.map((f) => {
                  const val = fields[f.id];
                  if (f.type === "checkbox") {
                    return (
                      <div key={f.id} className="flex items-center gap-2 py-0.5 text-sm">
                        <span className={`font-bold ${val ? "text-[#EF4444]" : "text-[#B8B9BD]"}`}>{val ? "✓" : "—"}</span>
                        <span className={val ? "" : "text-[#B8B9BD]"} style={{ color: val ? 'var(--text-primary)' : undefined }}>{f.label}</span>
                      </div>
                    );
                  }
                  if (!val) return null;
                  return (
                    <div key={f.id} className="flex justify-between py-1 text-sm">
                      <span style={{ color: 'var(--text-secondary)' }}>{f.label}</span>
                      <span className="font-semibold text-right max-w-[60%]">{String(val)}</span>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Signature & Save Template */}
            <div className="border-t-2 pt-5 mt-4 space-y-4" style={{ borderColor: 'var(--border-light)' }}>
              <SignaturePad label="Engineer's Signature" value={signature} onChange={setSignature} />
              <SaveTemplateButton formType={config?.type || ""} getFormData={() => ({ project, fields })} />
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
              <button onClick={() => setStep(1)} className="brutalist-btn-outline flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Previous</button>
              <div className="flex gap-3">
                <button onClick={handlePreview} disabled={previewing} className="brutalist-btn-outline flex items-center gap-2">
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview PDF
                </button>
                <button data-testid="gas-generic-generate" onClick={handleGenerate} disabled={submitting} className="brutalist-btn flex items-center gap-2" style={{ backgroundColor: "#EF4444" }}>
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><FileDown className="w-4 h-4" /> Generate Document</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <BuyCreditsModal open={showModal} onClose={closeModal} creditType={modalCreditType} onBuy={buyCredits} />
      <PdfPreviewModal open={showPreview} onClose={() => { setShowPreview(false); if (previewUrl) URL.revokeObjectURL(previewUrl); }} pdfUrl={previewUrl} loading={previewing} />
    </div>
  );
}
